import { LatLon } from "./types";

const toRad = (deg: number) => (deg * Math.PI) / 180;
const toDeg = (rad: number) => (rad * 180) / Math.PI;

// Point a fraction of the way along the great circle from a to b.
export const interpolateGreatCircle = (
  a: LatLon,
  b: LatLon,
  fraction: number
): LatLon => {
  const lat1 = toRad(a.lat);
  const lon1 = toRad(a.lon);
  const lat2 = toRad(b.lat);
  const lon2 = toRad(b.lon);

  const delta =
    2 *
    Math.asin(
      Math.sqrt(
        Math.sin((lat2 - lat1) / 2) ** 2 +
          Math.cos(lat1) * Math.cos(lat2) * Math.sin((lon2 - lon1) / 2) ** 2
      )
    );
  if (delta === 0) return a;

  const p = Math.sin((1 - fraction) * delta) / Math.sin(delta);
  const q = Math.sin(fraction * delta) / Math.sin(delta);
  const x = p * Math.cos(lat1) * Math.cos(lon1) + q * Math.cos(lat2) * Math.cos(lon2);
  const y = p * Math.cos(lat1) * Math.sin(lon1) + q * Math.cos(lat2) * Math.sin(lon2);
  const z = p * Math.sin(lat1) + q * Math.sin(lat2);

  return {
    lat: toDeg(Math.atan2(z, Math.sqrt(x * x + y * y))),
    lon: toDeg(Math.atan2(y, x)),
  };
};

// Compass bearing from a to b, in degrees (0 = north, 90 = east).
export const bearing = (a: LatLon, b: LatLon): number => {
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const dLon = toRad(b.lon - a.lon);
  const y = Math.sin(dLon) * Math.cos(lat2);
  const x =
    Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
};

// Longitude jumps from 180 to -180 at the antimeridian, in the Pacific. A map
// line through points either side of it is drawn the long way round the
// world: a straight streak across the whole map. These two keep a line in one
// piece by letting longitude run past 180 (or below -180), which the map
// draws correctly.

// The copy of a longitude nearest to another one: -88 near 135 is 272.
export const nearestLongitude = (lon: number, reference: number): number =>
  lon + 360 * Math.round((reference - lon) / 360);

// A line's [lon, lat] points with each longitude continuing from the last.
export const unwrapLongitudes = (
  coordinates: number[][],
  startNear?: number
): number[][] => {
  let previous = startNear;
  return coordinates.map(([lon, lat]) => {
    const continued = previous === undefined ? lon : nearestLongitude(lon, previous);
    previous = continued;
    return [continued, lat];
  });
};
